<template>
  <div class="bgs-editor">
    <div v-if="!isSaveAvailable" style="margin-bottom: 24px;">
      <alert class="alerts-item" type="warning">
        Необходимо заполнить: тип БГ, сумма, контакты Заказчика, сроки
      </alert>
    </div>

    <div class="bgs-editor__content">
      <div class="bgs-editor__block">
        <divider class="primary-divider" orientation="left">Основные сведения</divider>
        <div class="bgs-editor__block-content">
          <row :gutter="16" class="bgs-editor__row">
            <i-col class="bgs-editor__cell" span="8">
              <span class="ms-help label">Тип БГ</span>
              <i-select v-model="source.bg_type_id" placeholder="Выберите тип">
                <i-option
                  v-for="item in bgsTypes"
                  :value="item.id"
                  :key="item.id"
                  :label="item.name"
                >
                  {{ item.name }}
                </i-option>
              </i-select>
            </i-col>
            <i-col class="bgs-editor__cell" span="8">
              <span class="ms-help label">Сумма</span>
              <input-number
                v-model="source.amount"
                :min="0"
                :formatter="costsFormatter"
                :parser="costsParser"
              />
            </i-col>
          </row>
        </div>
      </div>

      <div class="bgs-editor__block">
        <divider class="primary-divider" orientation="left">Особые условия</divider>
        <div class="bgs-editor__block-content">
          <div class="bgs-editor__row">
            <checkbox v-model="source.is_require_bank_list">
              Есть требования к перечню банков от Заказчика
            </checkbox>
            <bgs-resources
              v-if="source.is_require_bank_list"
              :source="source"
              :file-type-id="fileTypesConfig.bankList"
              style="margin-top: 8px;"
            />
          </div>
          <div class="bgs-editor__row">
            <checkbox v-model="source.is_require_agreement">
              Есть требования к согласованию БГ с Заказчиком до ее выпуска
            </checkbox>
            <bgs-resources
              v-if="source.is_require_agreement"
              :source="source"
              :file-type-id="fileTypesConfig.requirementsList"
              style="margin-top: 8px;"
            />
          </div>
          <div class="bgs-editor__row">
            <span class="ms-help label">
              Требования к форме предоставления БГ Заказчику
            </span>
            <i-select v-model="source.form" multiple>
              <i-option
                v-for="item in sources.forms"
                :value="item.key"
                :key="item.key"
                :label="item.name"
              >
                {{ item.name }}
              </i-option>
            </i-select>
          </div>
        </div>
      </div>

      <div class="bgs-editor__block">
        <divider class="primary-divider" orientation="left">Контакты Заказчика</divider>
        <div class="bgs-editor__block-content">
          <row :gutter="16" class="bgs-editor__row">
            <i-col class="bgs-editor__cell" span="24">
              <span class="ms-help label">ФИО</span>
              <i-input v-model="source.contact_fio" />
            </i-col>
          </row>
          <row :gutter="16" class="bgs-editor__row">
            <i-col class="bgs-editor__cell" span="8">
              <span class="ms-help label">Должность</span>
              <i-input v-model="source.contact_position" />
            </i-col>
            <i-col class="bgs-editor__cell" span="8">
              <span class="ms-help label">Телефон</span>
              <i-input
                v-model="source.contact_phone"
                @on-keydown="phoneHandler($event)"
              />
            </i-col>
            <i-col class="bgs-editor__cell" span="8">
              <span class="ms-help label">Эл. почта</span>
              <i-input v-model="source.contact_email" />
            </i-col>
          </row>
        </div>
      </div>

      <div class="bgs-editor__block">
        <divider class="primary-divider" orientation="left">Техническое описание</divider>
        <div class="bgs-editor__block-content">
          <row :gutter="16" class="bgs-editor__row">
            <i-col class="bgs-editor__cell" span="8">
              <span class="ms-help label">Дата начала действия</span>
              <date-picker
                format="dd.MM.yyyy"
                placeholder="Выберите дату"
                v-model="source.start_date"
                :options="startDateOptions"
              />
            </i-col>
            <i-col class="bgs-editor__cell" span="8">
              <span class="ms-help label">Дата окончания действия</span>
              <date-picker
                format="dd.MM.yyyy"
                placeholder="Выберите дату"
                v-model="source.end_date"
                :options="endDateOptions"
              />
            </i-col>
          </row>
          <row :gutter="16" class="bgs-editor__row">
            <i-col class="bgs-editor__cell" span="8">
              <span class="ms-help label">Банк, выдавший гарантию</span>
              <i-select v-model="source.bank_id" placeholder="Выберите банк">
                <i-option
                  v-for="item in banks"
                  :value="item.id"
                  :key="item.id"
                  :label="item.name"
                >
                  {{ item.name }}
                </i-option>
              </i-select>
            </i-col>
            <i-col class="bgs-editor__cell" span="8">
              <span class="ms-help label">Номер БГ</span>
              <i-input v-model="source.number" />
            </i-col>
            <i-col class="bgs-editor__cell" span="8">
              <span class="ms-help label"
                >Комиссия за выпуск БГ (% годовых)</span
              >
              <input-number v-model="source.fees_rate" :min="0" />
            </i-col>
          </row>
        </div>
      </div>

      <div class="bgs-editor__block">
        <divider class="primary-divider" orientation="left">Условия выдачи</divider>
        <div class="bgs-editor__block-content">
          <row :gutter="16" class="bgs-editor__row">
            <i-col class="bgs-editor__cell" span="24">
              <span class="ms-help label">Описание условий</span>
              <i-input v-model="source.conditions" type="textarea" :rows="3" />
              <alert style="margin-top: 16px;" class="alerts-item">
                В данный раздел прикладываются: Договор на выдачу БГ, Договор
                поручительства
              </alert>
              <bgs-resources
                :source="source"
                :file-type-id="fileTypesConfig.conditions"
                style="margin-top: 8px;"
              />
            </i-col>
          </row>
        </div>
      </div>

      <div class="bgs-editor__block">
        <divider class="primary-divider" orientation="left">Итоговая БГ</divider>
        <bgs-documents :source="source" />
      </div>

      <div class="bgs-editor__block">
        <divider class="primary-divider" orientation="left">Документы, переданные Заказчику</divider>
        <alert class="alerts-item">
          В данный раздел прикладываются: Сопроводительное письмо о направлении
          БГ, итоговый скан БГ, Счет на аванс (опционально), Доверенность на
          подписание БГ и Уведомление SWIFT/СПФС
        </alert>
        <bgs-resources
          :source="source"
          :file-type-id="fileTypesConfig.resultDocs"
          style="margin-top: 8px;"
        />
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { forms } from '@/config/bgs'
import { costsFormatter, costsParser, checkDigitalInput } from '@/utils'
import BgsResources from './bgs-resources.vue'
import BgsDocuments from './bgs-documents.vue'

const fileTypesConfig = {
  bankList: '00e8b30e-9e4d-4fa2-b7dc-8c867215ad45',
  requirementsList: '9abfcfb9-dd42-45e2-96b5-b2d05a68e827',
  conditions: '2c026be4-a3d4-43b7-aaeb-4f67881e1a1e',
  resultDocs: 'dce03b95-6f21-4eb7-9ae1-4e8a708cd882'
}

export default {
  props: ['source', 'is-save-available'],
  components: {
    BgsResources,
    BgsDocuments
  },
  data () {
    return {
      sources: {
        forms
      },
      fileTypesConfig
    }
  },
  computed: {
    bgsTypes () {
      return this.$store.state.contracts.bgsTypes || []
    },
    banks () {
      return this.$store.state.contracts.banks || []
    },
    startDateOptions () {
      return {
        disabledDate: date => {
          let end = this.source.end_date

          if (typeof end === 'string') {
            end = moment(end, 'DD.MM.YYYY')

            return date && date.valueOf() > end.valueOf()
          }

          if (!end) return false

          return date && date.valueOf() > end.valueOf()
        }
      }
    },
    endDateOptions () {
      return {
        disabledDate: date => {
          let start = this.source.start_date

          if (typeof start === 'string') {
            start = moment(start, 'DD.MM.YYYY')

            return date && date.valueOf() < start.valueOf()
          }

          if (!start) return false

          return date && date.valueOf() < start.valueOf()
        }
      }
    }
  },
  methods: {
    phoneHandler (e) {
      if (checkDigitalInput(e)) {
        e.preventDefault()
      }
    },
    costsFormatter,
    costsParser
  }
}
</script>

<style lang="less">
.bgs-editor {
  &__content {
    display: flex;
    flex-direction: column;
    gap: 32px;
  }

  &__block {
    &:first-child {
      .ivu-divider {
        margin-top: 0 !important;
      }
    }
  }

  &__row {
    &:not(:first-child) {
      margin-top: 16px;
    }
  }

  .ivu-input-number,
  .ivu-date-picker {
    width: 100%;
  }
}
</style>
